import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Save, Globe, Archive, ArchiveRestore } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'wellness', name: { fr: 'Bien-être', en: 'Wellness' } },
  { id: 'health', name: { fr: 'Santé', en: 'Health' } },
  { id: 'testimonials', name: { fr: 'Témoignages', en: 'Testimonials' } },
  { id: 'news', name: { fr: 'Actualités', en: 'News' } }
];

const LANGUAGES = ['fr', 'en', 'uk', 'ru', 'it', 'ro'];
const LANGUAGE_NAMES = {
  fr: 'Français',
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
  it: 'Italiano',
  ro: 'Română'
};

const AdminBlogManagement = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  const { language } = useLanguage();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('fr');
  const [showArchived, setShowArchived] = useState(false);
  
  const [form, setForm] = useState({
    slug: '',
    category: 'wellness',
    author: 'Équipe Opora Vie',
    read_time: '5 min',
    image_url: '',
    title_fr: '',
    title_en: '',
    title_uk: '',
    title_ru: '',
    title_it: '',
    title_ro: '',
    excerpt_fr: '',
    excerpt_en: '',
    excerpt_uk: '',
    excerpt_ru: '',
    excerpt_it: '',
    excerpt_ro: '',
    content_fr: '',
    content_en: '',
    content_uk: '',
    content_ru: '',
    content_it: '',
    content_ro: '',
    is_published: true
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (user && !user.is_admin) {
      toast.error('Accès réservé à l\'administrateur');
      navigate('/');
      return;
    }

    fetchArticles();
  }, [isAuthenticated, authLoading, user, navigate, token]);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog/articles?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      slug: '',
      category: 'wellness',
      author: 'Équipe Opora Vie',
      read_time: '5 min',
      image_url: '',
      title_fr: '',
      title_en: '',
      title_uk: '',
      title_ru: '',
      title_it: '',
      title_ro: '',
      excerpt_fr: '',
      excerpt_en: '',
      excerpt_uk: '',
      excerpt_ru: '',
      excerpt_it: '',
      excerpt_ro: '',
      content_fr: '',
      content_en: '',
      content_uk: '',
      content_ru: '',
      content_it: '',
      content_ro: '',
      is_published: true
    });
    setActiveLanguage('fr');
  };

  const openCreateModal = () => {
    setEditingArticle(null);
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (article) => {
    setEditingArticle(article);
    setForm({
      slug: article.slug || '',
      category: article.category || 'wellness',
      author: article.author || 'Équipe Opora Vie',
      read_time: article.read_time || '5 min',
      image_url: article.image_url || '',
      title_fr: article.title_fr || '',
      title_en: article.title_en || '',
      title_uk: article.title_uk || '',
      title_ru: article.title_ru || '',
      title_it: article.title_it || '',
      title_ro: article.title_ro || '',
      excerpt_fr: article.excerpt_fr || '',
      excerpt_en: article.excerpt_en || '',
      excerpt_uk: article.excerpt_uk || '',
      excerpt_ru: article.excerpt_ru || '',
      excerpt_it: article.excerpt_it || '',
      excerpt_ro: article.excerpt_ro || '',
      content_fr: article.content_fr || '',
      content_en: article.content_en || '',
      content_uk: article.content_uk || '',
      content_ru: article.content_ru || '',
      content_it: article.content_it || '',
      content_ro: article.content_ro || '',
      is_published: article.is_published !== false
    });
    setActiveLanguage('fr');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.slug || !form.title_fr) {
      toast.error('Le slug et le titre français sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(
          `${API}/admin/blog/articles/${editingArticle.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Article mis à jour');
      } else {
        await axios.post(
          `${API}/admin/blog/articles`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Article créé');
      }
      setModalOpen(false);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      await axios.delete(`${API}/admin/blog/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublished = async (article) => {
    try {
      await axios.put(
        `${API}/admin/blog/articles/${article.id}`,
        { is_published: !article.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(article.is_published ? 'Article dépublié' : 'Article publié');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleArchived = async (article) => {
    try {
      await axios.put(
        `${API}/admin/blog/articles/${article.id}`,
        { is_archived: !article.is_archived },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(article.is_archived ? 'Article restauré' : 'Article archivé');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  // Filter articles based on archive status
  const displayedArticles = articles.filter(article => 
    showArchived ? article.is_archived : !article.is_archived
  );

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-semibold">Gestion du Blog</h1>
              <p className="text-muted-foreground text-sm">
                {displayedArticles.length} article(s) {showArchived ? 'archivé(s)' : 'actif(s)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={showArchived ? "default" : "outline"}
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center gap-2 ${showArchived ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Voir les actifs' : 'Voir les archives'}
            </Button>
            {!showArchived && (
              <Button onClick={openCreateModal} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nouvel article
              </Button>
            )}
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-xl border border-sand-200 overflow-hidden">
          {displayedArticles.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-medium text-lg mb-2">
                {showArchived ? 'Aucun article archivé' : 'Aucun article'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {showArchived ? 'Les articles archivés apparaîtront ici' : 'Créez votre premier article de blog'}
              </p>
              {!showArchived && <Button onClick={openCreateModal}>Créer un article</Button>}
            </div>
          ) : (
            <div className="divide-y divide-sand-200">
              {displayedArticles.map((article) => (
                <div key={article.id} className={`p-4 hover:bg-sand-50 flex items-center gap-4 ${article.is_archived ? 'opacity-75' : ''}`}>
                  {/* Image preview */}
                  <div className="w-16 h-16 bg-sand-100 rounded-lg overflow-hidden shrink-0">
                    {article.image_url ? (
                      <img src={article.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium truncate">{article.title_fr}</h3>
                      {article.is_archived && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Archivé</span>
                      )}
                      {!article.is_published && !article.is_archived && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Brouillon</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="capitalize">{CATEGORIES.find(c => c.id === article.category)?.name.fr || article.category}</span>
                      <span>•</span>
                      <span>{article.author}</span>
                      <span>•</span>
                      <span>{article.read_time}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {/* Archive/Restore button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleArchived(article)}
                      title={article.is_archived ? 'Restaurer' : 'Archiver'}
                    >
                      {article.is_archived ? (
                        <ArchiveRestore className="w-4 h-4 text-sage-600" />
                      ) : (
                        <Archive className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                    {!article.is_archived && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => togglePublished(article)}
                          title={article.is_published ? 'Dépublier' : 'Publier'}
                        >
                          {article.is_published ? (
                            <Eye className="w-4 h-4 text-sage-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(article)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(article.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Slug (URL)</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="mon-article"
                />
                {form.title_fr && !form.slug && (
                  <button
                    type="button"
                    className="text-xs text-sage-600 mt-1 hover:underline"
                    onClick={() => setForm({ ...form, slug: generateSlug(form.title_fr) })}
                  >
                    Générer depuis le titre
                  </button>
                )}
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name.fr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Auteur</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                />
              </div>
              <div>
                <Label>Temps de lecture</Label>
                <Input
                  value={form.read_time}
                  onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                  placeholder="5 min"
                />
              </div>
              <div>
                <Label>URL de l'image</Label>
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => setForm({ ...form, is_published: v })}
              />
              <Label>Publié</Label>
            </div>

            {/* Language Tabs */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Contenu multilingue</span>
              </div>
              
              <div className="flex gap-1 mb-4 border-b">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLanguage(lang)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      activeLanguage === lang
                        ? 'border-sage-600 text-sage-600'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {LANGUAGE_NAMES[lang]}
                    {form[`title_${lang}`] && <span className="ml-1 text-sage-500">•</span>}
                  </button>
                ))}
              </div>

              {/* Language-specific content */}
              <div className="space-y-4">
                <div>
                  <Label>Titre ({LANGUAGE_NAMES[activeLanguage]})</Label>
                  <Input
                    value={form[`title_${activeLanguage}`] || ''}
                    onChange={(e) => setForm({ ...form, [`title_${activeLanguage}`]: e.target.value })}
                    placeholder={`Titre en ${LANGUAGE_NAMES[activeLanguage]}`}
                  />
                </div>
                
                <div>
                  <Label>Extrait ({LANGUAGE_NAMES[activeLanguage]})</Label>
                  <Textarea
                    value={form[`excerpt_${activeLanguage}`] || ''}
                    onChange={(e) => setForm({ ...form, [`excerpt_${activeLanguage}`]: e.target.value })}
                    placeholder={`Résumé court en ${LANGUAGE_NAMES[activeLanguage]}`}
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label>Contenu ({LANGUAGE_NAMES[activeLanguage]})</Label>
                  <Textarea
                    value={form[`content_${activeLanguage}`] || ''}
                    onChange={(e) => setForm({ ...form, [`content_${activeLanguage}`]: e.target.value })}
                    placeholder={`Contenu complet en ${LANGUAGE_NAMES[activeLanguage]}`}
                    rows={8}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogManagement;
